// services/apiService.js
import { API_CONFIG, getApiUrl } from '../config/apiconfig';
import {
  setAuthItem,
  getAuthItem,
  clearAuthSession,
} from './authSessionStore';
import { 
  generateSessionToken, 
  encryptData, 
  decryptData,
  validateDataIntegrity,
  logSecurityEvent
} from './securityService';
import { getPublicClientIpv4 } from "./organizationLoginIpService";
import { clearCredentialExpiryBannerDismissForSession } from "../utils/credentialGateUtils";
import { normalizeUserSessionModules } from "../utils/loginModulesUtils";
import { stampPlatformSuperAdminSession } from "../utils/permissionUtils";

// Generic API call function
const apiCall = async (url, options = {}) => {
  const config = {
    method: 'GET',
    headers: API_CONFIG.headers,
    timeout: API_CONFIG.timeout,
    ...options
  };

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.timeout);

    const response = await fetch(url, {
      ...config,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw error;
  }
};

// Login API service
export const loginUser = async (loginData) => {
  const url = getApiUrl('loginMfa');

  let clientIPv4 = "";
  try {
    clientIPv4 = (await getPublicClientIpv4()) || "";
  } catch {
    clientIPv4 = "";
  }
  
  const payload = {
    emailId: loginData.email,
    password: loginData.password,
    accessCodeIndex0: loginData.accessCodeIndexes[0],
    accessCodeIndex1: loginData.accessCodeIndexes[1],
    accessCodeIndex2: loginData.accessCodeIndexes[2],
    accessCodeValue0: parseInt(loginData.accessCodeValues[0]) || 0,
    accessCodeValue1: parseInt(loginData.accessCodeValues[1]) || 0,
    accessCodeValue2: parseInt(loginData.accessCodeValues[2]) || 0,
    clientIPv4,
    ClientIPv4: clientIPv4,
    ForReauth: !!loginData.forReauth,
    OrganizationId: loginData.organizationId || loginData.OrganizationId || "",
  };

  logSecurityEvent('login_attempt', { email: loginData.email });

  try {
    const response = await apiCall(url, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    logSecurityEvent('login_response_received', { success: response.success });
    return response;
  } catch (error) {
    logSecurityEvent('login_error', { error: error.message });
    throw error;
  }
};

// MFA phase 2 — verify OTP / authenticator code
export const verifyMfaLogin = async (mfaSessionToken, otpCode) => {
  const url = getApiUrl('loginMfaVerify');
  return apiCall(url, {
    method: 'POST',
    body: JSON.stringify({
      MfaSessionToken: mfaSessionToken,
      OtpCode: String(otpCode).trim(),
    }),
  });
};

/** Start MFA for entering an MFA-enabled organisation (after credentials / before org switch). */
export const startOrgEntryMfaChallenge = async ({
  staffId,
  emailId,
  organizationId,
  userName,
}) => {
  const url = getApiUrl("loginMfaOrgEntryChallenge");
  return apiCall(url, {
    method: "POST",
    body: JSON.stringify({
      StaffId: staffId,
      EmailId: emailId,
      OrganizationId: organizationId,
      UserName: userName || "",
    }),
  });
};

// Resend email OTP during MFA step
export const resendMfaEmailOtp = async (mfaSessionToken) => {
  const url = getApiUrl('loginMfaResendOtp');
  return apiCall(url, {
    method: 'POST',
    body: JSON.stringify({ MfaSessionToken: mfaSessionToken }),
  });
};

// Legacy login (non-MFA) — kept for rollback
export const loginUserLegacy = async (loginData) => {
  const url = getApiUrl('login');
  
  const payload = {
    emailId: loginData.email,
    password: loginData.password,
    accessCodeIndex0: loginData.accessCodeIndexes[0],
    accessCodeIndex1: loginData.accessCodeIndexes[1], 
    accessCodeIndex2: loginData.accessCodeIndexes[2],
    accessCodeValue0: parseInt(loginData.accessCodeValues[0]) || 0,
    accessCodeValue1: parseInt(loginData.accessCodeValues[1]) || 0,
    accessCodeValue2: parseInt(loginData.accessCodeValues[2]) || 0
  };

  logSecurityEvent('login_attempt', { email: loginData.email });

  try {
    const response = await apiCall(url, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    logSecurityEvent('login_response_received', { success: response.success });
    return response;
  } catch (error) {
    logSecurityEvent('login_error', { error: error.message });
    //console.error('Login API Error:', error);
    throw error;
  }
};

// Validate login response (supports MFA challenge success=2)
export const validateLoginResponse = (response) => {
  if (!response) {
    return { isValid: false, error: 'No response received' };
  }

  /* MFA required — credentials valid, show verification step */
  if (response.success === 2 && response.data) {
    return { isValid: true, requiresMfa: true, mfaChallenge: response.data };
  }

  if (response.success !== 1) {
    return { 
      isValid: false, 
      error: response.message || 'Login failed' 
    };
  }

  if (!response.data) {
    return { 
      isValid: false, 
      error: 'Invalid response data' 
    };
  }

  return { isValid: true, data: response.data };
};

// Store user data securely (Level 1 Security)
export const storeUserData = (userData) => {
  try {
    const priorSession = getUserData();
    const normalizedUserData = stampPlatformSuperAdminSession(
      normalizeUserSessionModules({ ...userData }),
      priorSession,
    );

    const sessionToken = generateSessionToken(normalizedUserData);
    const encryptedUserData = encryptData(normalizedUserData);

    setAuthItem('sessionToken', sessionToken);
    setAuthItem('userData', encryptedUserData);
    setAuthItem('isLoggedIn', 'true');
    setAuthItem('lastActivity', Date.now().toString());
    setAuthItem('sessionStart', Date.now().toString());

    setAuthItem('userName', normalizedUserData.UserName || '');
    setAuthItem('userRole', normalizedUserData.Role || '');
    setAuthItem('organizationName', normalizedUserData.OrganizationName || '');

    logSecurityEvent('user_data_stored', {
      userId: normalizedUserData.StaffId,
      role: normalizedUserData.Role,
    });

    return true;
  } catch (error) {
    logSecurityEvent('store_data_error', { error: error.message });
    //console.error('Error storing user data:', error);
    return false;
  }
};

// Get stored user data securely
export const getUserData = () => {
  try {
    // Check data integrity first
    if (!validateDataIntegrity()) {
      logSecurityEvent('integrity_check_failed', { action: 'clearing_session' });
      clearUserData();
      return null;
    }
    
    const encryptedUserData = getAuthItem('userData');
    if (!encryptedUserData) return null;
    
    // Decrypt user data
    const userData = decryptData(encryptedUserData);
    return userData;
  } catch (error) {
    logSecurityEvent('get_data_error', { error: error.message });
    //console.error('Error getting user data:', error);
    clearUserData();
    return null;
  }
};

// Validate current session
export const validateCurrentSession = () => {
  try {
    const isLoggedIn = getAuthItem('isLoggedIn');
    const sessionToken = getAuthItem('sessionToken');
    
    if (isLoggedIn !== 'true' || !sessionToken) {
      return false;
    }
    
    // Check data integrity
    if (!validateDataIntegrity()) {
      return false;
    }
    
    return true;
  } catch (error) {
    logSecurityEvent('session_validation_error', { error: error.message });
    //console.error('Session validation error:', error);
    return false;
  }
};

// Clear user data on logout
export const clearUserData = () => {
  clearAuthSession();
  clearCredentialExpiryBannerDismissForSession();

  try {
    sessionStorage.removeItem('SignatureBase64');
  } catch (e) {
    // ignore
  }

  logSecurityEvent('user_data_cleared', { reason: 'logout' });
};

// Periodic security check
export const performSecurityCheck = () => {
  if (!validateCurrentSession()) {
    logSecurityEvent('security_check_failed', { action: 'force_logout' });
    clearUserData();
    return false;
  }
  return true;
};

// Update activity timestamp
export const updateActivity = () => {
  if (getAuthItem('isLoggedIn') === 'true') {
    setAuthItem('lastActivity', Date.now().toString());
  }
};

export const logout = (navigate) => {
  try {
    clearUserData();

    logSecurityEvent('user_logout', {
      timestamp: Date.now(),
    });

    navigate('/login');
  } catch (error) {
    console.error('Logout error:', error);
    navigate('/login');
  }
};

export default {
  loginUser,
  loginUserLegacy,
  verifyMfaLogin,
  startOrgEntryMfaChallenge,
  resendMfaEmailOtp,
  validateLoginResponse,
  storeUserData,
  getUserData,
  clearUserData,
  validateCurrentSession,
  performSecurityCheck,
  updateActivity,
  logout
};